/* ═══════════════════════════════════════════════════════════════════════════════════════════════
   brisk-collection.js — desktop filter-bar panel controller

   Loaded in layout/theme.liquid alongside brisk-collection.css, gated to the collection and search
   templates. ~2 KB, no dependencies, no library.

   WHAT IT DOES, AND WHY IT HAS TO EXIST
   The filter sidebar is presented as a horizontal bar of dropdown buttons — at EVERY width since
   2026-08-06, when the theme's off-canvas mobile drawer was retired (merchant: "I don't like the
   idea of drawer"). The theme already has a generic expand/collapse for these headings — a jQuery
   document-delegated handler on `a[data-toggle-target]` (assets/theme.js:7063) — but it cannot drive
   a popover bar:

     · It animates the panel's `height`, which is right for an accordion and meaningless for an
       absolutely-positioned popover.
     · It has no exclusivity (two panels can be open), no outside-click close, and no Escape.
     · The server decides each group's initial open/closed state (`collapse_filters_method`, plus
       "force expand if this filter has active values"). A bar needs every panel closed on load.

   So this file intercepts the click in the CAPTURE phase and calls stopPropagation, which means the
   jQuery handler bound on `document` never sees it.

   🚨 `isBar()` USED TO BE A BREAKPOINT TEST AND IS NOW UNCONDITIONALLY TRUE. That is the whole of
   this file's share of the mobile change: with one presentation there is no width at which the
   theme's accordion should run, so there is no width at which the interception should be skipped.
   `mq` survives, but it now answers a narrower question — see its comment below.

   ⚠ NOTHING IN THIS FILE MAY DECLARE A LOCAL NAMED `open`. There is a module-level open() function, and
  `var` hoists to the top of its whole enclosing function — a local `open` therefore shadows it for the
  entire body, so a handler that both toggles something and opens a panel throws "open is not a
  function". This actually happened while writing the More-filters branch. Use nowOpen / openGroups /
  openBtns.

  ⚠ EVERY LISTENER IS ON `document`. The filter markup lives inside a `[data-ajax-container]` whose
   innerHTML theme.js replaces wholesale on each filter change, so anything bound to the elements
   themselves would be dead after the first interaction.
   ═══════════════════════════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var BAR = '.filter-container--bar';
  var OPEN = 'filter-group--bar-open';
  var ALIGN_RIGHT = 'filter-group--bar-align-right';

  /* 🚨 THIS IS NO LONGER "IS THE BAR ON". It is "is this a WIDE viewport", and it now governs exactly
     one thing: the "More filters" overflow, which only exists at ≥1000px — below that the row either
     scrolls sideways (phone) or wraps (tablet), so every facet is already reachable.
     Still 1000px so it matches the one remaining `@media (min-width: 1000px)` block in
     brisk-collection.css §7. If that block's breakpoint moves, move this with it. */
  var mq = window.matchMedia('(min-width: 1000px)');

  /* 🚨 A SECOND, DIFFERENT BREAKPOINT, AND CONFLATING THE TWO IS A BUG I ALREADY SHIPPED ONCE.
     This one is "is the panel a full-width sheet", which is the phone only (brisk-collection.css §8).
     `alignPanel` was first guarded with `mq` — but a pill-anchored panel exists from 768px up, so that
     silently disabled the off-screen flip for the whole 768-999px band, where the bar wraps to two
     rows and a right-hand pill's 250px panel really can run past the viewport. Measured at 768px: the
     layout width is 753 and the row ends at x=693. */
  var sheetMq = window.matchMedia('(max-width: 767.98px)');

  /* Remembered as the group's `label_*` class rather than a node reference or an index: the node is
     destroyed by every AJAX re-render, and indexes shift when a filter drops to zero results. The
     label class is derived from the filter's own name, so it survives both. */
  var openKey = null;

  /* Whether the "More filters" overflow is revealed. Remembered for the same reason openKey is: the
     AJAX swap replaces the whole `.filters__inner` subtree, so the class on the container survives but
     the toggle's aria-expanded does not, and a fresh render would come back collapsed mid-interaction. */
  var moreOpen = false;

  /* Kept as a function rather than deleted from its eight call sites: it names the precondition
     ("the bar is the UI") that every handler below genuinely depends on, and if a second
     presentation is ever introduced this is the single place that decides. */
  function isBar() {
    return true;
  }

  function keyOf(group) {
    var list = group.classList;
    for (var i = 0; i < list.length; i++) {
      if (list[i].indexOf('label_') === 0) return list[i];
    }
    /* The availability group has no `label_*` class. Without a fallback `openKey` stayed null, which
       made the outside-click and Escape guards below skip it entirely — it could be opened but not
       closed. This key is never used for re-selection after an AJAX swap (that group renders inline
       and has no panel), only as a truthy "something is open" marker. */
    if (group.classList.contains('filter-group--availability')) return 'filter-group--availability';
    return null;
  }

  /* Close the theme's sort menu WITHOUT dispatching a click.
     The obvious `$(btn).trigger('click')` is self-defeating: jQuery also invokes the element's native
     .click(), which dispatches a real bubbling click whose target is the sort button. The
     outside-click handler below then sees a target outside the open panel and immediately closes the
     panel that was just opened — the filter panel visibly flashed and shut.
     Setting the attribute is enough: the theme hides the menu purely with
     `.link-dropdown__button[aria-expanded=true] + .link-dropdown__options` (styles.css.liquid:6534).
     Skipping theme.js's handler also skips the inline `width` it writes, which §7 of the stylesheet
     pins with `width: auto !important` anyway. */
  function closeSortMenu() {
    var openBtns = document.querySelectorAll(
      BAR + ' .bc-fbar__sort .link-dropdown__button[aria-expanded="true"]'
    );
    for (var i = 0; i < openBtns.length; i++) openBtns[i].setAttribute('aria-expanded', 'false');
  }

  function headingOf(group) {
    return group.querySelector('.filter-group__heading');
  }

  function closeAll() {
    var openGroups = document.querySelectorAll(BAR + ' .' + OPEN);
    for (var i = 0; i < openGroups.length; i++) {
      openGroups[i].classList.remove(OPEN, ALIGN_RIGHT);
      var h = headingOf(openGroups[i]);
      if (h) h.setAttribute('aria-expanded', 'false');
    }
    openKey = null;
  }

  /* Flip a panel that would overflow the viewport to open leftwards instead. Reading the rect
     immediately after adding the class is safe and flash-free — the browser does not paint between
     statements in the same task.

     ⚠ NOT ON THE PHONE. Below 768px the panel is a full-width sheet pinned to both edges of the
     content column (brisk-collection.css §8), so it cannot overflow and there is nothing to flip.
     Worse, `--bar-align-right` sets `left: auto; right: 0`, which on a sheet would fight the `left: 0`
     that makes it full width. The guard is the breakpoint and not a class check because the class is
     what this function would be adding.
     ⚠ `sheetMq`, NOT `mq`. See its declaration — using the 1000px one here disabled the flip across
     the whole tablet band, which is where it is most needed. */
  function alignPanel(group) {
    if (sheetMq.matches) return;
    var panel = group.querySelector('.filter-group__items');
    if (!panel) return;
    var rect = panel.getBoundingClientRect();
    if (rect.right > window.innerWidth - 12) group.classList.add(ALIGN_RIGHT);
  }

  function open(group) {
    closeAll();
    closeSortMenu();
    group.classList.add(OPEN);
    alignPanel(group);
    var h = headingOf(group);
    if (h) h.setAttribute('aria-expanded', 'true');
    openKey = keyOf(group);
  }

  /* ── 1. Heading clicks (capture phase, so the theme's accordion never runs on desktop) ──────
     The same listener also watches the sort button, because the two controls must be mutually
     exclusive and only capture phase can see a sort click at all: theme.js's
     `toggleLinkDropdownButton` starts with `evt.stopPropagation()`, so by bubble phase the event is
     already dead and an open filter panel would sit there with the sort menu drawn under it. */
  document.addEventListener(
    'click',
    function (e) {
      if (!isBar()) return;
      var target = e.target;
      if (!target || !target.closest) return;

      /* Sort button: close any open filter panel, then let the theme's own handler run normally.
         Deliberately no preventDefault/stopPropagation here — theme.js owns opening this menu. */
      if (target.closest(BAR + ' .bc-fbar__sort .link-dropdown__button')) {
        closeAll();
        return;
      }

      /* "More filters": reveal the overflow facets. Handled here rather than in its own listener so it
         shares the capture-phase interception — it is a `.filter-group__heading`, so without this the
         branch below would treat it as a facet and try to open a panel it does not have. */
      var more = target.closest(BAR + ' [data-bc-more]');
      if (more) {
        e.preventDefault();
        e.stopPropagation();
        closeAll();
        closeSortMenu();
        var container = more.closest(BAR);
        /* ⚠ NOT named `open`. `var` hoists to the top of this whole listener, so a local `open` would
           shadow the outer open() function for the entire handler and the facet branch below would
           throw "open is not a function" on every pill click. */
        var nowOpen = container.classList.toggle('filter-container--bar-more');
        more.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
        moreOpen = nowOpen;
        return;
      }

      var heading = target.closest(BAR + ' #CollectionFilterForm .filter-group__heading');
      if (!heading) return;

      e.preventDefault();
      e.stopPropagation();

      var group = heading.closest('.filter-group');
      if (!group) return;

      if (group.classList.contains(OPEN)) {
        closeAll();
        /* Also close sort on the collapse branch. It used to be handled only in open(), so clicking
           an already-open heading to collapse it left the sort menu hanging open. */
        closeSortMenu();
        heading.setAttribute('aria-expanded', 'false');
      } else {
        open(group);
      }
    },
    true
  );

  /* ── 2. Click anywhere outside the open panel closes it ─────────────────────────────────────
     Bubble phase and deliberately passive about clicks INSIDE the panel: checkbox labels and the
     price-range slider must keep working, and their `change` events still reach theme.js's
     debounced AJAX handler untouched. */
  document.addEventListener('click', function (e) {
    if (!isBar() || !openKey) return;
    var target = e.target;
    if (!target || !target.closest) return;
    if (!target.closest('.' + OPEN)) closeAll();
  });

  /* ── 3. Escape closes and returns focus to the button that opened it ────────────────────────── */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !isBar() || !openKey) return;
    var group = document.querySelector(BAR + ' .' + OPEN);
    var heading = group ? headingOf(group) : null;
    closeAll();
    if (heading) heading.focus();
  });

  /* ── 4. CLOSE the panel once a filter has been applied ───────────────────────────────────────
     🚨 THIS USED TO RE-OPEN IT, AND THAT IS NOW THE BUG. The original rationale was sound while
     theme.js replaced the whole `.filters__inner` subtree on every change — the panel would have
     slammed shut mid-multi-select, so this hook put it back.
     Two things changed on 2026-08-08:
       1. theme.js no longer replaces the panel (`theme.bcSyncFilterPanel`), so nothing closes it
          any more — it just sat open over the results. On a phone that reads as "filters aren't
          working", because the shopper never sees the grid they just filtered. Merchant, same day:
          "Once filter is applied, the popup of filter should close."
       2. With the panel preserved, re-opening is pointless anyway: the nodes, their scroll position
          and focus were never destroyed.
     So the hook now CLOSES every open panel after a render, which is also the conventional
     behaviour for a filter dropdown: pick a value, see the results.
     ⚠ `openKey` is still cleared so the state machine cannot reopen a stale group later.
     ⚠ The overflow ("More filters") state is still restored — it is a layout state, not a popup,
       and losing it would reshuffle the bar under the shopper.
     ⚠ theme.js fires `cc:component:load` with jQuery's `.trigger()`, which dispatches through
     jQuery's own event system — a native addEventListener will NOT hear it. This must stay
     jQuery-bound. */
  function closeAfterRender() {
    /* Overflow state first — it is independent of whether a panel was open. */
    if (isBar() && moreOpen) {
      var container = document.querySelector(BAR);
      if (container) {
        container.classList.add('filter-container--bar-more');
        var mb = container.querySelector('[data-bc-more]');
        if (mb) mb.setAttribute('aria-expanded', 'true');
      }
    }
    closeAll();
    closeSortMenu();
    openKey = null;
  }

  function bindRenderHook() {
    if (!window.jQuery) return false;
    window.jQuery(document).on('cc:component:load', closeAfterRender);
    return true;
  }

  /* theme.js is deferred, so jQuery may not be present yet when this runs. */
  if (!bindRenderHook()) {
    document.addEventListener('DOMContentLoaded', bindRenderHook);
    window.addEventListener('load', bindRenderHook, { once: true });
  }

  /* ── 5. Crossing 1000px ──────────────────────────────────────────────────────────────────────
     No longer a switch between two presentations — the bar is both sides of this line now. What
     still has to be reset is the overflow state, which is meaningless below 1000px, and any inline
     height/opacity/display the theme's accordion left behind before this file started intercepting
     (or from a resize that interrupted its transition mid-flight). */
  function onBreakpointChange() {
    closeAll();
    /* Below 1000px the chip row scrolls and every facet is already reachable, so there is nothing to
       overflow and the toggle that reveals the overflow is hidden. Coming back up, the bar must not
       start out claiming four facets are revealed when the toggle reads collapsed. */
    moreOpen = false;
    var c = document.querySelector(BAR);
    if (c) c.classList.remove('filter-container--bar-more');
    var panels = document.querySelectorAll(BAR + ' .filter-group__items');
    for (var i = 0; i < panels.length; i++) {
      panels[i].style.height = '';
      panels[i].style.opacity = '';
      panels[i].style.display = '';
    }
  }

  if (mq.addEventListener) {
    mq.addEventListener('change', onBreakpointChange);
  } else if (mq.addListener) {
    mq.addListener(onBreakpointChange);
  }

  /* ── 6. Header description "Read more" ───────────────────────────────────────────────────────
     The description is clamped to three lines in CSS. The toggle is created here rather than in
     Liquid for one reason: whether it is needed depends on how the text WRAPS, which only the browser
     knows. Rendering it server-side would put a "Read more" link under every two-line description.

     Measured with `scrollHeight > clientHeight + 1` — the +1 absorbs sub-pixel line-height rounding,
     which otherwise reports a 1px overflow on text that visibly fits.

     ⚠ Runs on load and on resize (the wrap point moves with the column width), but NOT on AJAX
     re-render: the header sits outside every `[data-ajax-container]`, so filtering never replaces it. */
  var CLAMP_OPEN = 'bc-header__desc--open';
  var SENTENCE_CLAMP = 'bc-header__desc--sentence-clamped';
  var MOBILE = '(max-width: 767.98px)';
  var KEEP_SENTENCES = 2;

  /* ── Two sentences on mobile ──────────────────────────────────────────────────────────────────
     Merchant, 2026-08-06: "on the phone, this description should be two sentences and then view more."

     🚨 WHY THIS CANNOT BE A LINE CLAMP. Measured at 386px, this store's two-sentence description sets to
     FIVE lines, so the CSS `-webkit-line-clamp: 3` was cutting it in the middle of sentence one. And no
     line count can mean "two sentences": the value that shows two short ones cuts a long one in half.
     So the split happens on sentence boundaries, in JS, where the sentences actually are.

     ⚠ IT ONLY TOUCHES A SINGLE-PARAGRAPH DESCRIPTION. The field is rich text and may hold lists, links
     or several paragraphs; splitting HTML on punctuation would corrupt any of those. So this bails out
     unless the description is exactly one <p> of plain text, and the 3-line CSS clamp handles everything
     else. Narrow and safe beats clever and occasionally destructive.
     ⚠ The regex requires the sentence end to be followed by whitespace, so "Italy and Portugal." splits
     but "$99.99" and "e.g." do not.
     ⚠ Idempotent: it unwraps its own previous work before re-measuring, because it re-runs on resize and
     the viewport can cross the breakpoint in either direction. */
  function sentenceClamp(desc) {
    var prev = desc.querySelector('.bc-header__desc-rest');
    if (prev) {
      /* Put the tail back as plain text so the next run measures the original, not a nested wrap. */
      prev.replaceWith(document.createTextNode(prev.textContent));
      desc.normalize();
    }
    desc.classList.remove(SENTENCE_CLAMP);

    if (!window.matchMedia(MOBILE).matches) return false;

    var kids = desc.children;
    if (kids.length !== 1 || kids[0].tagName !== 'P') return false;
    var para = kids[0];
    if (para.children.length) return false;          /* links or <em> — leave it alone */

    var text = para.textContent;
    var re = /[.!?]+(?=\s)/g;
    var ends = [];
    var m;
    while ((m = re.exec(text)) !== null) ends.push(m.index + m[0].length);
    if (ends.length <= KEEP_SENTENCES - 1) return false;   /* two sentences or fewer: nothing to hide */

    var cut = ends[KEEP_SENTENCES - 1];
    var rest = document.createElement('span');
    rest.className = 'bc-header__desc-rest';
    rest.textContent = text.slice(cut);
    para.textContent = text.slice(0, cut);
    para.appendChild(rest);
    desc.classList.add(SENTENCE_CLAMP);
    return true;
  }

  /* ── Animated toggle ──────────────────────────────────────────────────────────────────────────
     The class flip alone SNAPS between 2 lines and full height (merchant: "the content should
     expand smoothly"). Height is animated from the measured start to the measured end, and the
     open class stays ON for the whole ride in BOTH directions — line-clamp must not re-truncate
     the text while the box is still shrinking past it. The final class state lands when the
     transition ends (or the 400ms fallback fires — transitionend is droppable on iOS if the tab
     loses focus mid-animation). Reduced motion gets the plain flip. */
  function animateDescToggle(desc, toOpen) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      desc.classList.toggle(CLAMP_OPEN, toOpen);
      return;
    }
    if (desc._bcSettle) desc._bcSettle();          /* re-tap mid-animation: settle, then re-measure */

    var startH = desc.offsetHeight;
    desc.classList.toggle(CLAMP_OPEN, toOpen);
    var endH = desc.offsetHeight;
    if (endH === startH) return;

    desc.classList.add(CLAMP_OPEN);                /* text stays whole while the box moves */
    desc.style.height = startH + 'px';
    desc.style.overflow = 'hidden';
    void desc.offsetHeight;                        /* commit the start frame */
    desc.style.transition = 'height 240ms ease';
    desc.style.height = endH + 'px';

    var timer = null;
    function settle() {
      desc.removeEventListener('transitionend', onEnd);
      if (timer) clearTimeout(timer);
      desc._bcSettle = null;
      desc.style.transition = '';
      desc.style.height = '';
      desc.style.overflow = '';
      desc.classList.toggle(CLAMP_OPEN, toOpen);
    }
    function onEnd(e) { if (e.propertyName === 'height') settle(); }
    desc._bcSettle = settle;
    desc.addEventListener('transitionend', onEnd);
    timer = setTimeout(settle, 400);
  }

  function setupReadMore() {
    var desc = document.querySelector('[data-bc-clamp]');
    if (!desc) return;

    var aside = desc.parentElement;
    var btn = aside.querySelector('.bc-header__more');
    var isOpen = desc.classList.contains(CLAMP_OPEN);

    /* On mobile the sentence split decides whether a toggle is needed; the line measurement below cannot,
       because a sentence-clamped description has no overflow to measure. */
    /* ⛔ THE SENTENCE SPLIT IS RETIRED. It shipped, and it was the wrong mechanism: this store's
       description is exactly two sentences, so it correctly found nothing to hide and no toggle ever
       appeared — leaving five lines above the grid on a phone. The mobile rule is now a 2-line CSS clamp
       (section 1 of brisk-collection.css), which fires on any copy including copy that is already short.
       The function is still called so it UNWINDS anything a previously cached script left behind. */
    sentenceClamp(desc);
    var clamped = false;

    if (clamped) {
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bc-header__more';
        aside.appendChild(btn);
        btn.addEventListener('click', function () {
          var nowOpen = desc.classList.toggle(CLAMP_OPEN);
          btn.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
          btn.textContent = nowOpen ? 'View less' : 'View more';
        });
      }
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      btn.textContent = isOpen ? 'View less' : 'View more';
      return;
    }

    /* Measure against the clamped state, or an already-expanded description always looks like it
       overflows and the button could never be removed on resize. */
    if (isOpen) desc.classList.remove(CLAMP_OPEN);
    var overflows = desc.scrollHeight > desc.clientHeight + 1;
    if (isOpen) desc.classList.add(CLAMP_OPEN);

    if (!overflows && !isOpen) {
      if (btn) btn.remove();
      return;
    }
    if (btn) return;

    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bc-header__more';
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = 'View more';
    btn.addEventListener('click', function () {
      var nowOpen = !desc.classList.contains(CLAMP_OPEN);
      animateDescToggle(desc, nowOpen);
      btn.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
      btn.textContent = nowOpen ? 'View less' : 'View more';
    });
    aside.appendChild(btn);
  }

  /* ⛔ syncClothRail DELETED 2026-08-08 — the Shop by cloth rail it served is gone (see the
     tombstone in sections/main-collection.liquid). It re-derived the rail's hrefs from the live URL
     after each AJAX filter change, because the server-rendered `url_to_add` links froze at page
     load and resurrected removed cloths. Nothing emits `.bc-cloth` any more. */

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(setupReadMore, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupReadMore);
  } else {
    setupReadMore();
  }
  /* Fonts land after DOMContentLoaded and change where the text wraps. */
  window.addEventListener('load', setupReadMore, { once: true });
  window.addEventListener('resize', onResize);

  /* POISONED-PAGE-CACHE GUARD (2026-08-17). Shopify's intermittent "Liquid error (…): internal"
     episodes (§13a-0) can fail a render that then gets CAPTURED INTO THE PAGE CACHE — observed
     today on /collections/design-your-shirt: the bare URL served an error line in place of grid
     items for several minutes while cache-busted requests rendered fine. If the SERVED page
     arrived poisoned, fetch-and-swap or reload once with a cache-buster; a sessionStorage
     one-shot flag prevents any loop. Text nodes only, scripts excluded (the innerHTML
     false-positive is documented) and scoped to the ajax containers so it can never react to a
     script constant elsewhere in the page. */
  function bcPoisonCheck() {
    try {
      var containers = document.querySelectorAll('[data-ajax-container]');
      var found = false;
      for (var i = 0; i < containers.length && !found; i++) {
        var walker = document.createTreeWalker(containers[i], NodeFilter.SHOW_TEXT, null);
        var node;
        while ((node = walker.nextNode())) {
          var tag = node.parentNode && node.parentNode.nodeName;
          if (tag === 'SCRIPT' || tag === 'STYLE') continue;
          if (node.nodeValue && node.nodeValue.indexOf('Liquid error (snippets/') !== -1) { found = true; break; }
        }
      }
      if (found && !sessionStorage.getItem('bcLqReload')) {
        sessionStorage.setItem('bcLqReload', '1');
        /* 2026-08-28: a reload — with or without `bcfresh=` — re-serves the cached copy, because
           unknown query params are not part of Shopify's page-cache key (measured: ~25 ms cache
           hits). `view=` IS part of the key and an unknown suffix falls back to the default
           template (measured on both themes), so fetch the section with a unique `view=` and swap
           the grid in place — no reload, no URL change. */
        var root = document.querySelector('[data-bc-inf]');
        var grid = document.querySelector('[data-product-grid]');
        var sid = root && root.dataset.bcSection;
        if (!sid) {
          var sec = grid && grid.closest('.shopify-section');
          if (sec && sec.id) sid = sec.id.replace(/^shopify-section-/, '');
        }
        if (!grid || !sid || !window.fetch) { location.reload(); return; }
        var u = new URL(location.href);
        u.searchParams.set('section_id', sid);
        u.searchParams.set('view', 'bcfresh' + Date.now().toString(36));
        fetch(u.toString(), { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('status ' + r.status)); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-product-grid]');
            if (!fresh || fresh.textContent.indexOf('Liquid error (snippets/') !== -1) throw new Error('fresh render poisoned too');
            grid.innerHTML = fresh.innerHTML;
            var freshRoot = doc.querySelector('[data-bc-inf]');
            if (root && freshRoot && freshRoot.dataset.bcNext) root.dataset.bcNext = freshRoot.dataset.bcNext;
            try {
              if (window.theme && theme.ProductBlockManager) theme.ProductBlockManager.loadImages(grid);
              if (window.theme && theme.initAnimateOnScroll) theme.initAnimateOnScroll();
            } catch (e2) { /* cards are in; images re-init is best effort */ }
            if (window.console) console.warn('[brisk] served page was a cached failed render - grid replaced from a fresh render');
          })
          .catch(function () {
            if (window.console) console.warn('[brisk] served page was a cached failed render and the fresh render failed too - keeping the page');
          });
      } else if (!found) {
        sessionStorage.removeItem('bcLqReload');
      }
    } catch (e) { /* a guard must never be the thing that breaks the page */ }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bcPoisonCheck);
  } else {
    bcPoisonCheck();
  }
})();
